class StickyPageNav extends HTMLElement {
  connectedCallback() {
    this.anchorLinks = Array.from(this.querySelectorAll('a[href^="#"]'));
    this._rafPending = false;

    // Expose own height as CSS variable for scroll-margin-top calculations
    new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        '--sticky-page-nav-height',
        `${this.offsetHeight}px`
      );
      this._updateTop();
    }).observe(this);

    // Track body class changes (header pin/unpin) to reposition immediately
    this._mutationObserver = new MutationObserver(() => this._updateTop());
    this._mutationObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Track scroll to reposition on every frame
    this._scrollHandler = () => {
      if (this._rafPending) return;
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._updateTop();
        this._rafPending = false;
      });
    };
    window.addEventListener('scroll', this._scrollHandler, { passive: true });

    this._updateTop();

    this.anchorLinks.forEach((link) =>
      link.addEventListener('click', this.handleAnchorClick.bind(this))
    );

    this.initScrollSpy();

    if (window.location.hash) {
      setTimeout(() => this.scrollToHash(window.location.hash, 'auto'), 100);
    }

    if (Shopify.designMode) {
      this.closest('.shopify-section')?.addEventListener('shopify:section:load', () => {
        this.anchorLinks = Array.from(this.querySelectorAll('a[href^="#"]'));
        this.anchorLinks.forEach((link) =>
          link.addEventListener('click', this.handleAnchorClick.bind(this))
        );
        this.initScrollSpy();
        this._updateTop();
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._scrollHandler);
    this._mutationObserver?.disconnect();
    this._observer?.disconnect();
  }

  _getHeaderBottom() {
    const headerEl = document.querySelector('.header-section .header');
    if (!headerEl) return 0;

    // getBoundingClientRect() accounts for CSS transforms:
    // - header fully visible: returns its real bottom edge in the viewport
    // - header hidden via translateY(-100%): bottom approaches 0
    // - intermediate state (topbar/announcement gone, header sticky but not yet
    //   "header-scrolled"): returns the sticky header's actual bottom — not the
    //   full header-group height, which was the bug with the CSS approach
    let bottom = headerEl.getBoundingClientRect().bottom;

    // When is-hide-nav, the <header> element's height hasn't changed but its
    // .header__bottom nav is translated upward inside it, so subtract nav height
    // to reflect the actual visible bottom of the header
    if (document.body.classList.contains('is-hide-nav')) {
      const navH =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-navigation-height')
        ) || 0;
      bottom -= navH;
    }

    return Math.max(0, bottom);
  }

  _updateTop() {
    this.style.top = `${this._getHeaderBottom()}px`;
  }

  getScrollOffset() {
    return this._getHeaderBottom() + this.offsetHeight;
  }

  handleAnchorClick(event) {
    const href = event.currentTarget.getAttribute('href');
    const target = document.querySelector(href);
    if (!target) return;

    event.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
    window.scrollTo({ top, behavior: 'smooth' });
    history.pushState(null, '', href);
  }

  scrollToHash(hash, behavior = 'smooth') {
    const target = document.querySelector(hash);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
    window.scrollTo({ top, behavior });
  }

  initScrollSpy() {
    if (this._observer) this._observer.disconnect();

    const targets = this.anchorLinks
      .map((link) => {
        const href = link.getAttribute('href');
        return href.startsWith('#') ? document.querySelector(href) : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    const setActive = (id) => {
      this.anchorLinks.forEach((link) => {
        const active = link.getAttribute('href') === `#${id}`;
        link.classList.toggle('sticky-page-nav__link--active', active);
        link.closest('.sticky-page-nav__item')?.classList.toggle(
          'sticky-page-nav__item--active',
          active
        );
      });
    };

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      {
        rootMargin: `-${this.getScrollOffset()}px 0px -60% 0px`,
        threshold: 0,
      }
    );

    targets.forEach((t) => this._observer.observe(t));
  }
}

customElements.define('sticky-page-nav', StickyPageNav);
